"use client";

import { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import { theme } from "@/lib/theme";
import Image from "next/image";

interface ChapterTitleProps {
  chapterNumber?: number | string;
  title: string;
  background?: "white" | "green" | "lightLime" | "gradient" | "transparent";
  customGradient?: string;
  image?: string;
  transparentBackground?: boolean; // New prop to disable background for transitions
}

export const ChapterTitle = ({
  chapterNumber,
  title,
  background = "green",
  customGradient,
  image,
  transparentBackground = false,
}: ChapterTitleProps) => {
  const ref = useRef<HTMLDivElement>(null);

  // Track scroll position relative to this element
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "start start"], // From bottom of viewport to top of viewport
  });

  // Map scroll progress to opacity: fade in over transition zone
  // 0 = not visible (before transition starts)
  // 1 = fully visible (at chapter start)
  const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);

  // Map scroll progress to scale: zoom in during transition
  // Starts slightly smaller and scales to full size
  const scale = useTransform(scrollYProgress, [0, 1], [0.85, 1]);

  // Determine background based on prop
  const getBackground = () => {
    if (transparentBackground) {
      return "transparent";
    }
    if (customGradient) {
      return customGradient;
    }
    switch (background) {
      case "transparent":
        return "transparent";
      case "white":
        return theme.colors.white;
      case "lightLime":
        return theme.colors.lightLime;
      case "gradient":
        return `linear-gradient(180deg, ${theme.colors.limeGreen} 0%, ${theme.colors.limeGreenFaded} 100%)`;
      case "green":
      default:
        return theme.colors.limeGreen;
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        marginTop: {
          xs: "-64px",
          sm: "-80px",
        },
        marginBottom: 0,
        textAlign: "center",
        paddingTop: {
          xs: "96px",
          sm: "128px",
        },
        paddingBottom: {
          xs: "96px",
          sm: "128px",
        },
        paddingLeft: {
          xs: "24px",
          sm: "32px",
        },
        paddingRight: {
          xs: "24px",
          sm: "32px",
        },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      {/* Animated background layer that fades in with scroll */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: getBackground(),
          opacity,
        }}
      />
      {/* Content layer above the animated background */}
      <motion.div
        style={{
          maxWidth: theme.containerWidth.page,
          width: "100%",
          position: "relative",
          zIndex: 1,
          scale,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: theme.colors.gray[500],
            marginBottom: "8px",
          }}
        >
          Chapter {chapterNumber}
        </Typography>

        <Typography
          variant="h2"
          component="h2"
          sx={{
            color: theme.colors.black,
            margin: "0 auto",
            width: theme.containerWidth.inset,
          }}
        >
          {title}
        </Typography>
      </motion.div>

      {image && (
        <motion.div
          style={{
            marginTop: "32px",
            maxWidth: "900px",
            width: "100%",
            position: "relative",
            zIndex: 1,
            scale,
          }}
        >
          <Image
            src={image}
            alt=""
            width={900}
            height={400}
            unoptimized={image.toLowerCase().endsWith(".gif")}
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </motion.div>
      )}
    </Box>
  );
};
